import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Loader2, Users } from "lucide-react";
import Reveal from "../components/ui/Reveal";
import SEO from "../components/seo/SEO";
import UniversitySelect from "../components/ui/UniversitySelect";
import { useUniversities } from "../hooks/useUniversities";

import { API_BASE_URL } from "../config/api";

// ── Success Screen ────────────────────────────────────────────────────────────
function SuccessScreen({ name }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      style={{ textAlign: "center", padding: "var(--space-12) var(--space-6)" }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "var(--color-primary-soft)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto var(--space-6)",
        }}
      >
        <CheckCircle size={36} style={{ color: "var(--color-primary)" }} />
      </motion.div>

      <h2 className="heading-3" style={{ marginBottom: "var(--space-3)" }}>
        You're on the list, {name}! 🎉
      </h2>
      <p className="body-large" style={{ color: "var(--color-text-secondary)", maxWidth: 400, margin: "0 auto var(--space-6)" }}>
        We'll notify you the moment CampusGenie opens its doors. Keep an eye on your inbox  something big is coming.
      </p>

      <div style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 20px",
        borderRadius: "var(--radius-full)",
        background: "var(--color-primary-soft)",
        color: "var(--color-primary)",
        fontSize: "var(--text-sm)",
        fontWeight: "var(--font-semibold)",
      }}>
        <Users size={16} />
        Join your classmates. Share CampusGenie
      </div>

      <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "var(--space-4)", flexWrap: "wrap" }}>
        {[
          { label: "Share on X", href: "https://twitter.com/intent/tweet?text=Just%20joined%20the%20CampusGenie%20waitlist!%20The%20app%20that%20gets%20Nigerian%20students%20in%20and%20through%20university.%20%F0%9F%8E%93%20campusgenie.app%2Fwaitlist&via=campusgenie" },
          { label: "Share on WhatsApp", href: "https://wa.me/?text=Just%20joined%20the%20CampusGenie%20waitlist!%20The%20app%20built%20for%20Nigerian%20students%20from%20JAMB%20to%20graduation.%20Join%20me%20%E2%86%92%20campusgenie.app%2Fwaitlist" },
        ].map(({ label, href }) => (
          <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            {label}
          </a>
        ))}
      </div>
    </motion.div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// ── Field wrapper  defined OUTSIDE WaitlistPage to prevent remount on every keystroke ──
function Field({ id, label, error, children }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label} <span style={{ color: "var(--color-primary)" }}>*</span>
      </label>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            style={{ color: "#ef4444", fontSize: "var(--text-sm)", marginTop: "6px" }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function WaitlistPage() {
  const { universities, loading: uniLoading } = useUniversities();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    school: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: typeof e === "string" ? e : e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.email.trim()) {
      e.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = "Enter a valid email address";
    }
    if (!form.phone.trim()) {
      e.phone = "Phone number is required";
    } else if (!/^(\+?234|0)[789]\d{9}$/.test(form.phone.trim().replace(/\s/g, ""))) {
      e.phone = "Enter a valid Nigerian phone number";
    }
    if (!form.school) e.school = "Please select your university";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("loading");

    if (!API_BASE_URL) {
      setStatus("error");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          school_name: form.school,
          phone_number: form.phone.trim().replace(/\s/g, ""),
        }),
      });

      if (!response.ok) throw new Error("Request failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <SEO
        title="Join the Waitlist CampusGenie"
        description="Be among the first Nigerian students to access CampusGenie, the app that stays with you from JAMB prep to university graduation. Join the waitlist today."
        url="/waitlist"
      />

      {/* Hero */}
      <section
        className="hero"
        style={{ minHeight: "auto", paddingBottom: "var(--space-8)" }}
      >
        <div className="container">
          <Reveal>
            <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto" }}>
              <div className="hero-badge" style={{ display: "inline-flex", marginBottom: "var(--space-5)" }}>
                <span className="hero-badge-dot" />
                Early Access
              </div>
              <h1 className="heading-hero hero-title" style={{ fontSize: "clamp(2rem, 5vw, 3.25rem)" }}>
                Be first in line when{" "}
                <span className="text-gradient">CampusGenie</span> launches.
              </h1>
              <p className="body-large" style={{ color: "var(--color-text-secondary)", marginTop: "var(--space-4)" }}>
                The single app that stays with Nigerian students from JAMB prep to university graduation. Join thousands already on the waitlist.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Form Section */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <Reveal delay={0.1}>
              <div className="contact-form-card">
                <AnimatePresence mode="wait">
                  {status === "success" ? (
                    <SuccessScreen key="success" name={form.firstName} />
                  ) : (
                    <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <h2 className="heading-4" style={{ marginBottom: "var(--space-2)" }}>
                        Join the Waitlist
                      </h2>
                      <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-sm)", marginBottom: "var(--space-7)" }}>
                        All fields are required. We'll notify you as soon as access opens.
                      </p>

                      {status === "error" && (
                        <div style={{
                          padding: "12px 16px",
                          background: "rgba(239,68,68,0.08)",
                          border: "1px solid rgba(239,68,68,0.25)",
                          borderRadius: "var(--radius-lg)",
                          color: "#ef4444",
                          fontSize: "var(--text-sm)",
                          marginBottom: "var(--space-5)",
                        }}>
                          {!API_BASE_URL
                            ? "Waitlist service is unavailable right now. Please try again later."
                            : "We couldn't add you to the waitlist. Please try again."}
                        </div>
                      )}

                      <form onSubmit={handleSubmit} noValidate>
                        {/* Name row */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                          <Field id="firstName" label="First Name" error={errors.firstName}>
                            <input
                              id="firstName"
                              type="text"
                              className={`form-input ${errors.firstName ? "input-error" : ""}`}
                              placeholder="e.g. Chisom"
                              value={form.firstName}
                              onChange={set("firstName")}
                              autoComplete="given-name"
                            />
                          </Field>
                          <Field id="lastName" label="Last Name" error={errors.lastName}>
                            <input
                              id="lastName"
                              type="text"
                              className={`form-input ${errors.lastName ? "input-error" : ""}`}
                              placeholder="e.g. Adebayo"
                              value={form.lastName}
                              onChange={set("lastName")}
                              autoComplete="family-name"
                            />
                          </Field>
                        </div>

                        <Field id="email" label="Email Address" error={errors.email}>
                          <input
                            id="email"
                            type="email"
                            className={`form-input ${errors.email ? "input-error" : ""}`}
                            placeholder="you@email.com"
                            value={form.email}
                            onChange={set("email")}
                            autoComplete="email"
                          />
                        </Field>

                        <Field id="phone" label="Phone Number" error={errors.phone}>
                          <input
                            id="phone"
                            type="tel"
                            className={`form-input ${errors.phone ? "input-error" : ""}`}
                            placeholder="e.g. 08012345678"
                            value={form.phone}
                            onChange={set("phone")}
                            autoComplete="tel"
                            inputMode="tel"
                          />
                        </Field>

                        <Field id="school" label="University / Polytechnic" error={errors.school}>
                          <UniversitySelect
                            universities={universities}
                            loading={uniLoading}
                            value={form.school}
                            onChange={set("school")}
                            error={!!errors.school}
                          />
                        </Field>

                        <button
                          type="submit"
                          className="btn btn-primary btn-lg"
                          disabled={status === "loading"}
                          style={{ width: "100%", justifyContent: "center", marginTop: "var(--space-2)" }}
                        >
                          {status === "loading" ? (
                            <>
                              <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
                              Joining…
                            </>
                          ) : (
                            "Join the Waitlist →"
                          )}
                        </button>

                        <p style={{ textAlign: "center", fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "var(--space-4)" }}>
                          No spam, ever. Unsubscribe anytime.
                        </p>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>

            {/* Social proof */}
            <Reveal delay={0.2}>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "var(--space-6)", marginTop: "var(--space-8)", flexWrap: "wrap" }}>
                {[
                  { value: "1K+", label: "Already waiting" },
                  { value: "36+", label: "Universities represented" },
                  { value: "Free", label: "Always to students" },
                ].map((s) => (
                  <div key={s.label} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--font-bold)", color: "var(--color-primary)" }}>{s.value}</div>
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "2px" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
